import { cn } from "@/lib/utils";
import { LucideIcon, MoreVertical } from "lucide-react";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ACTION_I18N_KEYS } from "./action-i18n-keys";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

export type TableRowAction = {
  id: string;
  label: string;
  icon: LucideIcon;
  onSelect?: () => void;
  disabled?: boolean;
  destructive?: boolean;
  hidden?: boolean;
  /** Contenu personnalisé (drawer, etc.) affiché à la place d'un item standard. */
  render?: () => ReactNode;
};

export const tableRowActionMenuItemClassName =
  "h-auto w-full cursor-pointer justify-start gap-2 rounded-sm px-2 py-1.5 text-sm font-normal shadow-none focus:bg-slate-100 focus:text-accent-foreground";

export function TableRowActionMenuItemContent({
  icon: Icon,
  label,
  destructive = false,
}: {
  icon: LucideIcon;
  label: string;
  destructive?: boolean;
}) {
  return (
    <>
      <Icon
        className={cn(
          "h-4 w-4 shrink-0",
          destructive ? "text-red-500" : "text-[#64748B]",
        )}
        aria-hidden
      />
      <span className={cn(destructive && "text-red-500")}>{label}</span>
    </>
  );
}

export function TableRowActionsMenu({ actions }: { actions: TableRowAction[] }) {
  const { t } = useTranslation();
  const visibleActions = actions.filter((action) => !action.hidden);

  if (visibleActions.length === 0) {
    return null;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={(event) => event.stopPropagation()}
                aria-label={t(ACTION_I18N_KEYS.menuLabel)}
              >
                <MoreVertical className="h-4 w-4 text-gray-500" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            {t(ACTION_I18N_KEYS.menuLabel)}
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent
          align="end"
          className="min-w-[12rem]"
          onClick={(event) => event.stopPropagation()}
        >
          {visibleActions.map((action) => {
            if (action.render) {
              return <div key={action.id}>{action.render()}</div>;
            }

            const Icon = action.icon;
            return (
              <DropdownMenuItem
                key={action.id}
                disabled={action.disabled}
                className={cn(
                  tableRowActionMenuItemClassName,
                  action.destructive && "text-red-500 focus:text-red-500",
                )}
                onClick={(event) => {
                  event.stopPropagation();
                  action.onSelect?.();
                }}
              >
                <Icon
                  className={cn(
                    "h-4 w-4 shrink-0",
                    action.destructive ? "text-red-500" : "text-[#64748B]",
                  )}
                  aria-hidden
                />
                {action.label}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </TooltipProvider>
  );
}
